import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import Button from "../../components/Button";
import DatePicker from "../../components/DatePicker";

export interface SignOffDateDialogProps {
  open: boolean;
  /** Stored date, in any format `Date` accepts — normalised to `YYYY-MM-DD` for the picker. */
  date: string | null;
  fieldLabel: string;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (date: string) => void;
}

/** Stored dates arrive as `YYYY-MM-DD HH:mm:ss`; the picker speaks bare `YYYY-MM-DD`. */
export const toPickerDate = (date: string | null): string | null => (date ? date.slice(0, 10) : null);

const SignOffDateDialog: FC<SignOffDateDialogProps> = ({ open, date, fieldLabel, saving, error, onClose, onSave }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState<string | null>(toPickerDate(date));

  // Re-seed whenever the dialog opens so a cancelled edit does not leak into the next one.
  useEffect(() => {
    if (open) setValue(toPickerDate(date));
  }, [open, date]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{`${t("training_pages.edit_sign_off_date")} — ${fieldLabel}`}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 1 }}>
          <DatePicker
            label={t("training_pages.sign_off_date")}
            value={value}
            onChange={setValue}
            disableFuture
            disabled={saving}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("cancel")}
        </Button>
        <Button color="primary" disabled={saving || !value} onClick={() => value && onSave(value)}>
          {t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SignOffDateDialog;
